import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { keys, useTemplates } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import type { Locale } from "../../lib/types";
import { SectionHeader } from "../ui/Badges";
import { RULE_CODES } from "./AutomationRules";

const VARIABLES = ["KidName", "GuardianName", "MinutesLeft", "BandId", "StartTime", "EndTime", "ExitTime", "Duration", "AddedMinutes", "ExtensionPrice", "OvertimeRate", "AirLockDoor"];

export function TemplateEditor() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isManager } = useAuth();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const { data } = useTemplates();
  const area = useRef<HTMLTextAreaElement>(null);

  const [code, setCode] = useState<string>(RULE_CODES[1]);
  const [lang, setLang] = useState<Locale>(locale);
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState("");
  const [phone, setPhone] = useState("");

  const current = data?.find((x) => x.code === code && x.locale === lang);
  useEffect(() => {
    setLang(locale);
  }, [locale]);
  useEffect(() => {
    setBody(current?.body ?? "");
  }, [current?.id, current?.body]);

  // Live preview with sample values (debounced).
  useEffect(() => {
    if (!body) {
      setPreview("");
      return;
    }
    const h = setTimeout(() => {
      // Substituted locally so the preview is instant and reflects unsaved edits.
      setPreview(body.replace(/\{(\w+)\}/g, (_m, name: string) => SAMPLE[lang][name] ?? `{${name}}`));
    }, 150);
    return () => clearTimeout(h);
  }, [body, code, lang]);

  function insert(name: string) {
    const el = area.current;
    const token = `{${name}}`;
    if (!el) return setBody((b) => b + token);
    const { selectionStart: s, selectionEnd: e } = el;
    setBody(body.slice(0, s) + token + body.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + token.length, s + token.length);
    });
  }

  async function save(): Promise<boolean> {
    try {
      await api.put(`/whatsapp/templates/${code}/${lang}`, { body });
      await qc.invalidateQueries({ queryKey: keys.templates });
      return true;
    } catch (e) {
      toastError(e);
      return false;
    }
  }

  async function saveAndTest() {
    if (!(await save())) return;
    if (!phone.trim()) return toast(t("whatsapp.templateSaved"), "success");
    try {
      const r = await api.post<{ message: { status: string } }>(`/whatsapp/templates/${code}/test`, { phone, locale: lang });
      toast(t("whatsapp.testSent", { status: r.message.status }), "success");
    } catch (e) {
      toastError(e);
    }
  }

  return (
    <section className="card space-y-2.5 p-4" aria-label={t("whatsapp.templates")}>
      <SectionHeader title={`📝 ${t("whatsapp.templates")}`} right={<span className="text-[10px] font-bold text-gray-500">{t("whatsapp.livePreview")}</span>} />
      <div className="flex flex-wrap items-center gap-2">
        <select className="field !w-auto flex-1 !py-1.5" value={code} onChange={(e) => setCode(e.target.value)} aria-label={t("whatsapp.selectedTemplate")}>
          {RULE_CODES.map((c) => <option key={c} value={c}>{t(`rules.${c}.title`)}</option>)}
        </select>
        <div role="tablist" className="flex rounded-lg bg-gray-100 p-0.5 text-[11px] font-black">
          {(["ar", "en"] as Locale[]).map((l) => (
            <button key={l} type="button" role="tab" aria-selected={lang === l} onClick={() => setLang(l)} className={`rounded-md px-2.5 py-1 ${lang === l ? "bg-white text-cosmic-purple shadow-sm" : "text-gray-500"}`}>{l === "ar" ? "العربية" : "English"}</button>
          ))}
        </div>
      </div>
      <textarea ref={area} className="field min-h-24 font-mono leading-relaxed" dir={lang === "ar" ? "rtl" : "ltr"} rows={4} value={body} onChange={(e) => setBody(e.target.value)} readOnly={!isManager} aria-label={t("whatsapp.templateBody")} />
      <div className="flex flex-wrap gap-1.5 text-[10px] font-bold">
        {VARIABLES.map((v) => (
          <button key={v} type="button" disabled={!isManager} onClick={() => insert(v)} className="num rounded border border-purple-200 bg-purple-50 px-2 py-0.5 text-cosmic-purple hover:bg-purple-100 disabled:opacity-50">{`{${v}}`}</button>
        ))}
      </div>
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-2.5 text-xs" dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="mb-1 text-[10px] font-black uppercase text-emerald-700">{t("whatsapp.preview")}</div>
        <p className="whitespace-pre-wrap text-gray-800">{preview || "—"}</p>
      </div>
      {isManager && (
        <div className="flex flex-wrap gap-2">
          <input className="field num !w-44 !py-1.5 font-mono" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("whatsapp.testPhone")} aria-label={t("whatsapp.testPhone")} />
          <button type="button" className="btn-dark flex-1" onClick={saveAndTest} disabled={body.trim().length < 5}>{phone.trim() ? t("whatsapp.saveAndTest") : t("whatsapp.save")}</button>
        </div>
      )}
    </section>
  );
}

const SAMPLE: Record<Locale, Record<string, string>> = {
  en: { KidName: "Rayan", GuardianName: "Reem", MinutesLeft: "10", BandId: "WB-104", StartTime: "05:45 PM", EndTime: "06:45 PM", ExitTime: "06:40 PM", Duration: "55m", AddedMinutes: "30", ExtensionPrice: "3.000", OvertimeRate: "0.500", AirLockDoor: "Air-Lock" },
  ar: { KidName: "ريان", GuardianName: "ريم", MinutesLeft: "10", BandId: "WB-104", StartTime: "05:45 PM", EndTime: "06:45 PM", ExitTime: "06:40 PM", Duration: "55 دقيقة", AddedMinutes: "30", ExtensionPrice: "3.000", OvertimeRate: "0.500", AirLockDoor: "بوابة الخروج" },
};
